import { http } from "./client";
import type { PageParams } from "@/types/api";
import type {
  Employee,
  EmployeeBranchTransfer,
  EmployeeDepartmentTransfer,
  EmployeeManagerChange,
  EmployeeStatus,
  EmployeeUpcomingEvent,
  EmploymentType,
  SalaryType,
  User,
} from "@/types/domain";

export interface EmployeeFilters extends PageParams {
  department_id?: string;
  /** Exact job title, case-insensitive. */
  designation?: string;
  branch_id?: string;
  // unassigned=true narrows to employees with no branch at all - a distinct
  // filter from branch_id, which always names one specific branch.
  unassigned?: boolean;
  manager_id?: string;
  status?: EmployeeStatus | "";
  salary_type?: SalaryType | "";
  employment_type?: EmploymentType | "";
  on_roll?: boolean;
  // without_login=true narrows to on-roll employees with no linked user
  // account yet - the "provision logins for imported employees" list. See
  // backend EmployeeFilter.WithoutLogin.
  without_login?: boolean;
}

// Money fields are numbers here, not the decimal strings Employee reads them
// back as: the API accepts a JSON number for writes (internal/transport/http/
// handler/employee.go's employeeRequest) and only ever returns money as a
// string, to keep floats out of the read path where they'd invite arithmetic.
type MoneyFields = "basic_salary" | "hra" | "conveyance_allowance" | "medical_allowance" | "special_allowance" | "daily_rate" | "hourly_rate";

// branch_id/branch are deliberately excluded here - the backend's own PUT
// /employees/:id ignores branch_id even if sent (see employeeRequest.apply's
// doc comment on the Go side), so this type doesn't offer it either. Branch
// reassignment after creation only ever goes through employeesApi.transferBranch.
//
// department_id/manager_id are NOT excluded from this type - unlike
// branch_id, they're still legitimately part of the create payload (see
// EmployeeCreateInput usage in EmployeeFormPage's create mode). But since a
// recent backend change, PUT /employees/:id now silently ignores them too,
// the same as branch_id always has - so EmployeeFormPage's edit mode must
// strip them from the payload it actually sends to update() itself, rather
// than relying on the type to prevent it. Reassignment after creation goes
// through employeesApi.transferDepartment/changeManager below.
export type EmployeeInput = Partial<
  Omit<Employee, "id" | "full_name" | "gross_monthly" | "department" | "cost_center_id" | "branch_id" | "branch" | "other_allowances" | MoneyFields> & {
    [K in MoneyFields]: number;
  } & {
    other_allowances: { code: string; name: string; amount: number; taxable: boolean; prorate: boolean }[];
  }
>;

// The one exception: an initial branch may be chosen at onboarding (recorded
// as an "Initial Assignment" history row server-side, not a silent field
// set) - see EmployeesApi.create below.
export type EmployeeCreateInput = EmployeeInput & {
  branch_id?: string;
  /** Probation or Regular (migration 0236); a probation sends its period in
   *  months, or its custom end date. */
  employment_status?: "probation" | "regular";
  probation_months?: number;
  probation_end_date?: string;
};

export interface ImportResult {
  created: number;
  skipped: number;
  failures?: {
    row: number;
    employee_code: string;
    message: string;
    // fields lets the editable importer highlight the exact cell(s) a row
    // failed on instead of just showing `message` as a paragraph - see
    // backend ImportError.Fields's own doc comment. Absent for an older
    // server or a failure with no field-level breakdown (a plain row-level
    // rejection still has `message`).
    fields?: { field: string; rule?: string; message: string }[];
  }[];
  // login_accounts_created is always 0 today: Import creates employee
  // records only. notice explains that and points at the follow-up flow
  // (createLogin below) - see backend ImportResult's own doc comment.
  login_accounts_created: number;
  notice?: string;
}

// EmployeeImportRow is one row of an import batch: an EmployeeCreateInput
// (the same shape a brand-new employee is created with, branch_id included -
// see EmployeeCreateInput's own doc comment) plus the ORIGINAL spreadsheet
// row number the editable importer's preview showed for it. The backend
// reports every failure (and, on a partial retry, every success) against
// this same number - see internal/transport/http/handler/employee.go's
// importEmployeeRow - so a correction always lands on the row the user
// actually edited, not the row's position in whatever subset of the file a
// retry happens to resubmit.
export type EmployeeImportRow = EmployeeCreateInput & { row: number };

// EmployeeLoginInput is the optional "Create login access" section of the
// web Add Employee form, and the whole body of a standalone login grant for
// an existing employee. email defaults to the employee's own email when
// omitted (create-with-login only); role_id defaults to the Employee role
// when omitted. password is optional - the backend generates and returns a
// one-time temporary password when left blank, exactly like Users & Roles'
// own Add User form.
export interface EmployeeLoginInput {
  email?: string;
  role_id?: string;
  password?: string;
}

export interface EmployeeWithLoginResult {
  employee: Employee;
  user: User;
  temporary_password?: string;
  notice?: string;
}

export const employeesApi = {
  list: (filters: EmployeeFilters = {}) => http.getPage<Employee>("/employees", filters),
  get: (id: string) => http.get<Employee>(`/employees/${id}`),
  // Employee Self Service "My Profile" - always the caller's own record.
  me: () => http.get<Employee>("/employees/me"),
  // Admin/HR set or clear an employee's profile picture (multipart, field
  // "photo"). Allowed whatever the company's "employees may change their own
  // picture" switch says - that switch only limits employees themselves.
  uploadPhoto: (id: string, file: File) => {
    const form = new FormData();
    form.append("photo", file);
    return http.post<Employee>(`/employees/${id}/photo`, form);
  },
  removePhoto: (id: string) => http.delete<Employee>(`/employees/${id}/photo`),
  // The code the Add Employee form pre-fills. first: the company has no
  // employee yet, so the code must be typed (later ones continue from it).
  nextCode: () => http.get<{ code: string; first: boolean }>("/employees/next-code"),
  create: (input: EmployeeCreateInput) => http.post<Employee>("/employees", input),
  // Web Add Employee form's "Create login access" checkbox - employee and
  // login provisioned atomically (requires both employee.write and
  // user.write; the API 403s otherwise, same double gate as /onboard).
  createWithLogin: (input: EmployeeCreateInput & { login: EmployeeLoginInput }) =>
    http.post<EmployeeWithLoginResult>("/employees/with-login", input),
  // Provisions a login for an employee that already exists (e.g. after a
  // CSV import, or a retry once the login half of createWithLogin failed) -
  // never creates a second employee record.
  createLogin: (employeeId: string, input: EmployeeLoginInput = {}) =>
    http.post<EmployeeWithLoginResult>(`/employees/${employeeId}/login`, input),
  update: (id: string, input: EmployeeInput) => http.put<Employee>(`/employees/${id}`, input),
  remove: (id: string) => http.delete<void>(`/employees/${id}`),
  import: (employees: EmployeeImportRow[]) => http.post<ImportResult>("/employees/import", { employees }),
  headcount: () =>
    http.get<{ by_status: Record<EmployeeStatus, number>; on_roll: number }>("/employees/headcount"),
  upcomingEvents: (params: { within_days?: number } = {}) =>
    http.get<EmployeeUpcomingEvent[]>("/employees/upcoming-events", params),
  // Branch transfer - see EmployeeInput's doc comment for why this is
  // separate from update().
  transferBranch: (id: string, input: { branch_id: string; effective_date: string; reason?: string }) =>
    http.patch<Employee>(`/employees/${id}/branch`, input),
  branchHistory: (id: string) => http.get<EmployeeBranchTransfer[]>(`/employees/${id}/branch-history`),
  // Department transfer - mirrors transferBranch above. The generic
  // update() no longer accepts department_id (see EmployeeInput's doc
  // comment), so a department reassignment after creation only ever goes
  // through this endpoint.
  transferDepartment: (id: string, input: { department_id: string; effective_date: string; reason?: string }) =>
    http.patch<Employee>(`/employees/${id}/department`, input),
  // Charges the employee's pay and expenses to one cost center of their
  // department; null clears it so the department default applies.
  setCostCenter: (id: string, costCenterId: string | null) =>
    http.put<{ employee_id: string; cost_center_id: string | null }>(`/employees/${id}/cost-center`, { cost_center_id: costCenterId }),
  departmentHistory: (id: string) => http.get<EmployeeDepartmentTransfer[]>(`/employees/${id}/department-history`),
  // Reporting manager change - manager_id may be null/omitted to clear the
  // manager entirely (a legitimate "reports to department head" state), not
  // just to reassign to another manager.
  changeManager: (id: string, input: { manager_id: string | null; effective_date: string; reason?: string }) =>
    http.patch<Employee>(`/employees/${id}/manager`, input),
  managerHistory: (id: string) => http.get<EmployeeManagerChange[]>(`/employees/${id}/manager-history`),
  // Archived tab - lets an accidental remove() be found and reversed via
  // restore() below without anyone needing direct database access.
  listArchived: (filters: Pick<EmployeeFilters, "search" | "department_id" | "branch_id" | "page" | "per_page"> = {}) =>
    http.getPage<Employee>("/employees/archived", filters),
  restore: (id: string) => http.post<void>(`/employees/${id}/restore`),
};
